import React, { useEffect, useReducer, useRef } from 'react';
import { first, isEmpty, isEqual, isNil } from 'lodash';
import { BodyScrollEvent, IRowNode } from '@ag-grid-community/core';

import { InputSuggest } from 'src/common-ui/index';
import { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';

import {
  getListDataValidValues,
  getValidValues,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { ValidValuesEditorProps, ValidValuesEditorState, deselectAll, selectAll } from './ValidValuesEditor';
import { ClientDataApi, ListDataApi } from 'configuration-schemas/confdefnView';
import { EditableGridHeaderEditorBaseProps } from './TextValidationHeaderEditor';
import { Tooltip } from '@material-ui/core';
import {
  MISMATCHED_OPTIONS_TEXT,
  ROW_SELECTION_REQUIRED_TEXT,
  ROW_SELECTIONS_UPDATING_TEXT,
} from 'src/utils/Domain/Constants';
import { style } from 'typestyle';
import { important } from 'csx';
interface ValidValuesHeaderEditorProps extends ValidValuesEditorProps, EditableGridHeaderEditorBaseProps {
  getDataConfig: (row: IRowNode, configuredApi: ClientDataApi) => ClientDataApi;
  /** Using unprocessed dataApi for header editors instead because
   * during column header generation, there are no selected rows to populate dataApi params */
  unprocessedDataConfig: ClientDataApi;
}

// Local component reducer actions
type UpdateMenuVisibility = {
  type: 'updateMenuVisibility';
  payload: {
    showMenu: boolean;
  };
};
type SetupSingleSelect = {
  type: 'setupSingleSelect';
  payload: {
    loaded: boolean;
    validValues: Suggestion[];
    selectedOption: Suggestion | undefined;
    useLabelAsValue: boolean;
  };
};
type SetupMultiSelect = {
  type: 'setupMultiSelect';
  payload: {
    loaded: boolean;
    validValues: Suggestion[];
    selectedOptions: Suggestion[];
  };
};
type UpdateSelection = {
  type: 'updateSelection';
  payload: {
    showMenu: boolean;
    selectedOption: Suggestion;
  };
};
type UpdateMultiSelection = {
  type: 'updateMultiSelection';
  payload: {
    showMenu: boolean;
    selectedOptions: Suggestion[];
  };
};
type UpdateDataConfigs = {
  type: 'updateDataConfigs';
  payload: {
    dataConfigs: ClientDataApi[];
    dataConfigParamsMatch: boolean;
  };
};

type ValidValuesHeaderEditorActions =
  | SetupSingleSelect
  | SetupMultiSelect
  | UpdateSelection
  | UpdateMultiSelection
  | UpdateDataConfigs
  | UpdateMenuVisibility;

interface ValidValuesHeaderEditorState extends ValidValuesEditorState {
  showMenu: boolean;
  dataConfigs: (ClientDataApi | ListDataApi)[];
  dataConfigParamsMatch: boolean;
}

function componentReducer(
  state: ValidValuesHeaderEditorState,
  { type, payload }: ValidValuesHeaderEditorActions,
): ValidValuesHeaderEditorState {
  switch (type) {
    case 'setupSingleSelect': {
      const { loaded, validValues, selectedOption, useLabelAsValue } = payload;
      return {
        ...state,
        loaded,
        selectedOption,
        useLabelAsValue,
        validValues,
      };
    }
    case 'setupMultiSelect': {
      const { loaded, validValues, selectedOptions } = payload;
      return {
        ...state,
        loaded,
        selectedOptions,
        validValues,
      };
    }
    case 'updateSelection': {
      const { selectedOption, showMenu } = payload;
      return {
        ...state,
        valueChosen: true,
        selectedOption,
        showMenu,
      };
    }
    case 'updateMultiSelection': {
      const { selectedOptions, showMenu } = payload;
      return {
        ...state,
        valueChosen: true,
        selectedOptions,
        showMenu,
      };
    }
    case 'updateDataConfigs': {
      const { dataConfigs, dataConfigParamsMatch } = payload;
      return {
        ...state,
        dataConfigs,
        dataConfigParamsMatch,
      };
    }
    case 'updateMenuVisibility': {
      const { showMenu } = payload;
      return {
        ...state,
        showMenu,
      };
    }
    default:
      return state;
  }
}
export type SingleOrMultiRef = Suggestion;
/**
 * Verifies that all items selected have the same valid values options
 *
 * @param dataConfigs The currently selected grid items processed dataApis
 * @returns boolean
 */
export function selectedItemsOptionsMatch(dataConfigs: (ClientDataApi | ListDataApi)[]): boolean {
  if (isEmpty(dataConfigs) || dataConfigs.length === 1) {
    return true;
  }

  const firstItem = first(dataConfigs);
  // compare first item params with every other items params, beginning with second item in array
  const allItemsParamsMatch = dataConfigs.slice(1).every((dc) => isEqual(firstItem?.params, dc.params));
  return allItemsParamsMatch;
}

const headerMinWidth = style({
  $debugName: 'validvaluesheader-minwidthlimit',
  $nest: {
    '.select-container': {
      minWidth: important('75px'),
    },
    '.s5-MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      height: 'var(--s5-headitor-input-height)',
      width: 'unset',
      margin: 'var(--s5-headitor-input-horizontal-spacing)',
      // because of the shorter headitor row, removing padding
      // keeps the input for the textfield within the dropdown container
      padding: 0,
    },
  },
});

export const ValidValuesHeaderEditor = (props: ValidValuesHeaderEditorProps) => {
  const {
    allowEmptyOption,
    api,
    clientHandler,
    column,
    concatOptionValues,
    dataQa,
    getDataConfig,
    ignoreCache,
    includeCurrent,
    multiSelect,
    onApplyEdit,
    options,
    getSelectedItems,
    getCellsUpdating,
    returnSelectionObject,
    unprocessedDataConfig,
    value,
  } = props;

  const [state, dispatch] = useReducer(componentReducer, {
    dataConfigs: [],
    dataConfigParamsMatch: false,
    loaded: false,
    selectedOption: undefined,
    selectedOptions: [],
    showMenu: false,
    useLabelAsValue: false,
    validValues: [],
    valueChosen: false,
  });

  const {
    dataConfigs,
    dataConfigParamsMatch,
    selectedOption,
    showMenu,
    selectedOptions,
    useLabelAsValue,
    validValues,
  } = state;

  /** Tracks whether the user has interacted with the component (needed to accept deselect all on multi-select) */
  const hasInteractedRef = useRef(false);

  const formatSelection = (selection: Suggestion) => {
    if (!isNil(returnSelectionObject) && returnSelectionObject) {
      return selection;
    }
    return !useLabelAsValue ? selection.value : selection.label;
  };

  const formatSelections = (selections: Suggestion[]) => {
    return !isEmpty(selections)
      ? selectedOptions.map((option: Suggestion) => (!useLabelAsValue ? option.value : option.label))
      : [];
  };

  const onInputSuggestBlur = () => {
    dispatch({ type: 'updateMenuVisibility', payload: { showMenu: false } });
    // if user has interected with multi-select and then clicks away
    // this is valid, (deselectAll)
    if (props.multiSelect && hasInteractedRef.current) {
      const items = getSelectedItems();
      const formattedValue = formatSelections(selectedOptions);
      onApplyEdit(formattedValue, items, column.getColDef());
      // back to false
      hasInteractedRef.current = false;
    }
  };
  const onInputSuggestFocus = () => dispatch({ type: 'updateMenuVisibility', payload: { showMenu: true } });

  useEffect(() => {
    function bodyScrollEventListener(event: BodyScrollEvent) {
      if (event.direction === 'horizontal') {
        dispatch({ type: 'updateMenuVisibility', payload: { showMenu: false } });
      }
    }
    api.addEventListener('bodyScroll', bodyScrollEventListener);
    return () => api.removeEventListener('bodyScroll', bodyScrollEventListener);
  }, [api]);

  useEffect(() => {
    if (isEmpty(dataConfigs) || !dataConfigParamsMatch) {
      return;
    }

    // at this point we know all dataConfigs params match, safe to retrieve validValues

    const dataConfig = first(dataConfigs);
    if (isNil(dataConfig)) {
      return;
    }
    const insertEmptyOption = multiSelect ? false : allowEmptyOption;
    const fetchProm = dataConfig?.isListData
      ? getListDataValidValues(dataConfig)
      : getValidValues(getUrl(dataConfig), insertEmptyOption, concatOptionValues, ignoreCache);
    fetchProm
      .then((validValues: Suggestion[]) => {
        if (isNil(multiSelect) || multiSelect == false) {
          dispatch({
            type: 'setupSingleSelect',
            payload: {
              validValues,
              loaded: true,
              selectedOption: undefined,
              useLabelAsValue,
            },
          });
        } else {
          dispatch({
            type: 'setupMultiSelect',
            payload: {
              validValues,
              loaded: true,
              selectedOptions: [],
            },
          });
        }
      })
      .catch((err) => console.log(err));
  }, [
    allowEmptyOption,
    clientHandler,
    concatOptionValues,
    dataConfigs,
    dataConfigParamsMatch,
    ignoreCache,
    includeCurrent,
    multiSelect,
    options,
    value,
    useLabelAsValue,
  ]);

  useEffect(() => {
    const selectedItemsDataConfigs = getSelectedItems().map((si) => getDataConfig(si, unprocessedDataConfig));
    const dataConfigParamsMatch = selectedItemsOptionsMatch(selectedItemsDataConfigs);

    dispatch({
      type: 'updateDataConfigs',
      payload: {
        dataConfigs: selectedItemsDataConfigs,
        dataConfigParamsMatch,
      },
    });
  }, [getDataConfig, getSelectedItems, unprocessedDataConfig]);

  const onSelect = (selection: Suggestion) => {
    dispatch({
      type: 'updateSelection',
      payload: { selectedOption: selection, showMenu: false },
    });
  };

  const onMultiSelect = (selections: Suggestion[]) => {
    // handling "Select/Deselect All"
    if (selections.indexOf(selectAll) >= 0 || selections.indexOf(deselectAll) >= 0) {
      if (selections.length === validValues.length + 1) {
        dispatch({
          type: 'updateMultiSelection',
          payload: { selectedOptions: [], showMenu: false },
        });
      } else {
        dispatch({
          type: 'updateMultiSelection',
          payload: { selectedOptions: validValues, showMenu: false },
        });
      }
    } else {
      dispatch({
        type: 'updateMultiSelection',
        payload: { selectedOptions: selections, showMenu: false },
      });
    }
  };

  const onSingleSelection = (selection: Suggestion) => {
    onSelect(selection);
    const items = getSelectedItems();
    const formattedValue = formatSelection(selection);
    onApplyEdit(formattedValue, items, column.getColDef());
  };

  const onMultiSelection = (selections: Suggestion[]) => {
    hasInteractedRef.current = true;
    onMultiSelect(selections);
  };

  // determine options, selection(s), and handler based on single or multi select
  const onSelectHandler = isNil(multiSelect) ? onSingleSelection : undefined;
  const onMultiSelectHandler = isNil(multiSelect) ? undefined : onMultiSelection;
  const selected = isNil(multiSelect) ? selectedOption : selectedOptions;

  let inputOptions: Suggestion[];
  if (multiSelect) {
    if (validValues.length === selectedOptions.length) {
      inputOptions = [deselectAll].concat(validValues);
    } else {
      inputOptions = [selectAll].concat(validValues);
    }
  } else {
    inputOptions = validValues;
  }

  const noSelections = isEmpty(getSelectedItems());
  const isUpdating = getCellsUpdating().length > 0;
  const mismatchedSelectionsOptions = dataConfigParamsMatch === false;
  const disabled = noSelections || mismatchedSelectionsOptions || isUpdating;
  const menuIsOpen = !disabled && showMenu;

  let title = '';
  if (noSelections) {
    title = ROW_SELECTION_REQUIRED_TEXT;
  } else if (isUpdating) {
    title = ROW_SELECTIONS_UPDATING_TEXT;
  } else if (mismatchedSelectionsOptions) {
    title = MISMATCHED_OPTIONS_TEXT;
  }

  // Even though the height is already set in the `headerMinWidth` class style,
  // need to provide an override with the correct height style
  // to properly overwrite the `minHeight` style for multiSelects
  const styleOverride = multiSelect
    ? {
        // !removing this style will cause the multiselect headitor to be styled improperly
        height: 'var(--s5-headitor-input-height)',
      }
    : undefined;

  return (
    <Tooltip title={title}>
      <div className={headerMinWidth}>
        <InputSuggest
          validValues={inputOptions}
          multiSelect={multiSelect}
          selected={selected}
          onSelect={onSelectHandler}
          onMultiSelect={onMultiSelectHandler}
          dataQa={dataQa}
          disabled={disabled}
          onBlur={onInputSuggestBlur}
          onFocus={onInputSuggestFocus}
          placeholder=""
          allowPortal={true}
          popperWidth={column.getActualWidth() + 120}
          style={styleOverride}
        />
      </div>
    </Tooltip>
  );
};
